import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X, Sparkles, AlertTriangle, FileText, RefreshCw, Loader2, Shield, Target,
  Brain, TrendingUp, ListChecks, HelpCircle, ChevronRight, Copy, Printer, Check,
  Code, Flag, Handshake, AlertOctagon, NotebookPen,
} from 'lucide-react';
import { loggedGet, loggedMutation, apiClient } from './loggedFetch';
import logger from '../../lib/logger';
import WarRoomPlaybook from './WarRoomPlaybook';
import AIDisclaimerBanner from './AIDisclaimerBanner';
import './DisplacementReportModal.css';

/*
 * DisplacementReportModal — full Master-Architect / CIO-grade executive
 * briefing for a sales play in the 30-60-90 kanban. The report is
 * generated by Claude Opus 4.7 from the route at
 * POST /api/vendor-analysis/:vendor/plan/cards/:cardId/report and
 * persisted on super_admin_plans.displacement_report.
 *
 * This component renders the persisted JSON. It does NOT trigger
 * generation — PlanKanban owns that, both auto (on add) and manually
 * (via the Generate button on cards). The modal only re-fetches the
 * stored payload via GET so we can read the latest state on open.
 */

function DisplacementReportModal({ isOpen, vendor, card, onClose, onRegenerate }) {
  const cardId = card?.id;
  const queryClient = useQueryClient();
  const [copyState, setCopyState] = useState({ md: false, json: false });
  const [notesDraft, setNotesDraft] = useState(card?.notes || '');
  const [notesSaved, setNotesSaved] = useState(false);

  // Reset the draft whenever the card or modal is re-opened — otherwise
  // we'd leak one card's draft into another's open.
  useEffect(() => {
    setNotesDraft(card?.notes || '');
    setNotesSaved(false);
  }, [card?.id, isOpen, card?.notes]);

  const notesDirty = (card?.notes || '') !== notesDraft;

  const saveNotes = useMutation({
    mutationFn: loggedMutation(
      'DisplacementReportModal:save-notes',
      (notes) =>
        apiClient.put(
          `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards/${cardId}`,
          { notes }
        )
    ),
    onSuccess: () => {
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 1500);
      // Invalidate plan queries so the kanban + rollup pick up the new
      // notes (and the next regenerate sees them).
      queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan', vendor] });
      queryClient.invalidateQueries({ queryKey: ['vendor-analysis', 'plan-with-reports', vendor] });
    },
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['vendor-analysis', 'plan', vendor, 'report', cardId],
    queryFn: loggedGet(
      'DisplacementReportModal:fetch',
      cardId
        ? `/api/vendor-analysis/${encodeURIComponent(vendor)}/plan/cards/${cardId}/report`
        : null
    ),
    enabled: isOpen && !!cardId && !!vendor,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'generating' ? 4000 : false;
    },
  });

  const status = data?.status;
  const report = data?.report;

  const flashCopied = useCallback((key) => {
    setCopyState(s => ({ ...s, [key]: true }));
    setTimeout(() => setCopyState(s => ({ ...s, [key]: false })), 1500);
  }, []);

  const handleCopyMarkdown = useCallback(async () => {
    if (!report) return;
    const md = reportToMarkdown({ vendor, card, report, generatedAt: data?.generated_at, model: data?.model });
    try {
      await navigator.clipboard.writeText(md);
      flashCopied('md');
      logger.vendorAnalysis.info('DisplacementReportModal:copy-md', { card_id: cardId });
    } catch (err) {
      logger.vendorAnalysis.error('DisplacementReportModal:copy-md:failed', { card_id: cardId }, err);
    }
  }, [report, vendor, card, data, cardId, flashCopied]);

  const handleCopyJson = useCallback(async () => {
    if (!report) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify({
        vendor,
        card_title: card?.title,
        agency: card?.seed_payload?.agency,
        competitor: card?.seed_payload?.competitor,
        generated_at: data?.generated_at,
        model: data?.model,
        report,
      }, null, 2));
      flashCopied('json');
      logger.vendorAnalysis.info('DisplacementReportModal:copy-json', { card_id: cardId });
    } catch (err) {
      logger.vendorAnalysis.error('DisplacementReportModal:copy-json:failed', { card_id: cardId }, err);
    }
  }, [report, vendor, card, data, cardId, flashCopied]);

  // Wrap onRegenerate so dirty notes get persisted FIRST, then the
  // regenerate fires. This is the "edit your notes, click regenerate,
  // expect them to be in the new briefing" flow the user actually wants.
  const handleRegenerate = useCallback(async () => {
    if (notesDirty) {
      try {
        await saveNotes.mutateAsync(notesDraft);
      } catch (err) {
        // Save failed — let the user see the error and try again
        // rather than silently dropping their notes.
        return;
      }
    }
    onRegenerate(cardId);
  }, [notesDirty, notesDraft, saveNotes, onRegenerate, cardId]);

  const handlePrint = useCallback(() => {
    // Add a body-level print class so the @media print stylesheet can
    // hide the rest of the app and render the modal full-bleed.
    document.body.classList.add('drm-printing');
    const cleanup = () => {
      document.body.classList.remove('drm-printing');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    logger.vendorAnalysis.info('DisplacementReportModal:print', { card_id: cardId });
    window.print();
  }, [cardId]);

  if (!isOpen || !card) return null;

  return (
    <div className="drm-overlay" onClick={onClose}>
      <div className="drm-content" onClick={(e) => e.stopPropagation()}>
        <header className="drm-header">
          <div className="drm-title-block">
            <div className="drm-eyebrow">
              <Sparkles size={14} />
              <span>Master Architect · CIO Executive Briefing</span>
            </div>
            <div className="drm-title">{card.title}</div>
            <div className="drm-subtitle">
              <span>{vendor}</span>
              {card.seed_payload?.competitor && (
                <>
                  <ChevronRight size={12} />
                  <span>vs {card.seed_payload.competitor}</span>
                </>
              )}
              {card.seed_payload?.agency && (
                <>
                  <ChevronRight size={12} />
                  <span>{card.seed_payload.agency}</span>
                </>
              )}
            </div>
          </div>
          <div className="drm-header-actions">
            {status === 'ready' && report && (
              <>
                <button
                  className="drm-icon-btn"
                  onClick={handleCopyMarkdown}
                  title="Copy as markdown — paste into email, Notion, or a doc"
                >
                  {copyState.md ? <Check size={14} /> : <Copy size={14} />}
                  <span>{copyState.md ? 'Copied' : 'Copy as markdown'}</span>
                </button>
                <button
                  className="drm-icon-btn"
                  onClick={handleCopyJson}
                  title="Copy raw JSON payload"
                >
                  {copyState.json ? <Check size={14} /> : <Code size={14} />}
                  <span>{copyState.json ? 'Copied' : 'JSON'}</span>
                </button>
                <button
                  className="drm-icon-btn"
                  onClick={handlePrint}
                  title="Print or export to PDF via your browser"
                >
                  <Printer size={14} />
                  <span>Print</span>
                </button>
              </>
            )}
            <button
              className={`drm-icon-btn ${notesDirty ? 'drm-icon-btn--accent' : ''}`}
              onClick={handleRegenerate}
              title={
                notesDirty
                  ? 'Save your notes and regenerate the briefing with the new account intel'
                  : 'Regenerate with Claude Opus 4.7 (re-runs the briefing)'
              }
              disabled={status === 'generating' || saveNotes.isPending}
            >
              {saveNotes.isPending ? <Loader2 size={14} className="drm-spin" /> : <RefreshCw size={14} />}
              <span>{notesDirty ? 'Save notes & regenerate' : 'Regenerate'}</span>
            </button>
            <button className="drm-close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="drm-body">
          <NotesEditor
            value={notesDraft}
            onChange={setNotesDraft}
            onBlur={() => {
              if (notesDirty) saveNotes.mutate(notesDraft);
            }}
            isPending={saveNotes.isPending}
            isError={!!saveNotes.error}
            errorMessage={saveNotes.error?.message}
            saved={notesSaved}
            dirty={notesDirty}
            disabled={status === 'generating'}
            charLimit={2400}
          />

          {(isLoading || status === 'generating') && (
            <div className="drm-loading">
              <Loader2 size={20} className="drm-spin" />
              <div>
                <strong>Generating CIO-level executive briefing…</strong>
                <p>
                  Claude Opus 4.7 is synthesizing this play against
                  {card.seed_payload?.agency ? ` ${card.seed_payload.agency}'s` : ' the agency\'s'}
                  {' '}known IT install base, the competitive context,
                  and the AI-era readiness narrative.
                  This typically takes 30-90 seconds.
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="drm-error">
              <AlertTriangle size={18} />
              <div>
                <strong>Couldn't load the report: {error.message}</strong>
                <button className="drm-icon-btn" onClick={() => refetch()} style={{ marginTop: 8 }}>
                  <RefreshCw size={14} /> Retry
                </button>
              </div>
            </div>
          )}

          {status === 'failed' && (
            <div className="drm-error">
              <AlertTriangle size={18} />
              <div>
                <strong>Report generation failed.</strong>
                <p>{data?.error || 'Unknown error from Anthropic API.'}</p>
                <button className="drm-icon-btn" onClick={() => onRegenerate(cardId)} style={{ marginTop: 8 }}>
                  <RefreshCw size={14} /> Retry generation
                </button>
              </div>
            </div>
          )}

          {status === 'none' && (
            <div className="drm-empty">
              <FileText size={20} />
              <div>
                <strong>No briefing has been generated yet.</strong>
                <p>
                  Press <em>Regenerate</em> above to commission a Master
                  Architect-level executive briefing for this play.
                  Auto-generation runs on cards seeded from the
                  displacement table or renewal calendar.
                </p>
              </div>
            </div>
          )}

          {status === 'ready' && report && (
            <>
              <AIDisclaimerBanner />
              <ReportSections report={report} model={data?.model} generatedAt={data?.generated_at} />
            </>
          )}

          {status === 'ready' && report && (
            <WarRoomPlaybook
              vendor={vendor}
              card={card}
              mainReportReady={true}
            />
          )}
        </div>

        <footer className="drm-footer">
          <span className="drm-footer-meta">
            {data?.model
              ? `Generated by ${data.model} · `
              : ''}
            Verify all claims with the customer before committing in
            writing — LLMs can confidently misstate facts.
          </span>
        </footer>
      </div>
    </div>
  );
}

export function ReportSections({ report, model, generatedAt }) {
  if (!report) return null;
  const generatedLabel = generatedAt
    ? new Date(generatedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div className="drm-report">
      {generatedLabel && (
        <div className="drm-meta-row">
          <span>Briefing generated {generatedLabel}</span>
        </div>
      )}

      {report.executive_summary && (
        <Section icon={<Sparkles size={14} />} label="Executive summary" emphasis>
          <Prose text={report.executive_summary} />
        </Section>
      )}

      {report.status_quo_risk && (
        <Section icon={<AlertTriangle size={14} />} label="Why do anything? — cost of the status quo" tone="warn">
          <Prose text={report.status_quo_risk} />
        </Section>
      )}

      {report.vendor_differentiation && (
        <Section icon={<Target size={14} />} label="Why us? — distinctive value">
          <Prose text={report.vendor_differentiation} />
        </Section>
      )}

      {report.install_base_insights && (
        <Section icon={<Shield size={14} />} label="Install-base insights">
          <Prose text={report.install_base_insights} />
        </Section>
      )}

      {Array.isArray(report.business_line_capabilities) && report.business_line_capabilities.length > 0 && (
        <Section icon={<Brain size={14} />} label="Business-line capabilities the LoB cares about">
          <div className="drm-capability-grid">
            {report.business_line_capabilities.map((cap, i) => (
              <div key={i} className="drm-capability">
                <div className="drm-capability__head">{cap.capability || cap.label || `Capability ${i + 1}`}</div>
                {cap.lob_value && (
                  <div className="drm-capability__row">
                    <span className="drm-capability__label">LoB outcome</span>
                    <span>{cap.lob_value}</span>
                  </div>
                )}
                {cap.why_target_wins && (
                  <div className="drm-capability__row">
                    <span className="drm-capability__label">Why we win</span>
                    <span>{cap.why_target_wins}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {report.ai_era_readiness && (
        <Section icon={<TrendingUp size={14} />} label="AI-era readiness · 3-5 year horizon" emphasis>
          <Prose text={report.ai_era_readiness} />
        </Section>
      )}

      {report.migration_risk_assessment && (
        <Section icon={<AlertTriangle size={14} />} label="Migration risk · honest assessment" tone="warn">
          <Prose text={report.migration_risk_assessment} />
        </Section>
      )}

      {report.budget_efficiency && (
        <Section icon={<TrendingUp size={14} />} label="Budget efficiency over 3-5 years">
          <Prose text={report.budget_efficiency} />
        </Section>
      )}

      {/* Offensive-play-only: cold-start entry motion */}
      {report.entry_motion && (
        <Section icon={<Flag size={14} />} label="Entry motion · cold-start play" emphasis>
          {report.entry_motion.beachhead && (
            <div className="drm-subblock">
              <div className="drm-subblock__label">Beachhead deployment</div>
              <Prose text={report.entry_motion.beachhead} />
            </div>
          )}
          {report.entry_motion.procurement_vehicle && (
            <div className="drm-subblock">
              <div className="drm-subblock__label">Procurement vehicle</div>
              <Prose text={report.entry_motion.procurement_vehicle} />
            </div>
          )}
          {report.entry_motion.peer_references && (
            <div className="drm-subblock">
              <div className="drm-subblock__label">Peer references</div>
              {Array.isArray(report.entry_motion.peer_references) ? (
                <ul className="drm-questions">
                  {report.entry_motion.peer_references.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              ) : (
                <Prose text={report.entry_motion.peer_references} />
              )}
            </div>
          )}
          {report.entry_motion.time_to_first_value && (
            <div className="drm-subblock">
              <div className="drm-subblock__label">Time to first value</div>
              <Prose text={report.entry_motion.time_to_first_value} />
            </div>
          )}
        </Section>
      )}

      {/* Coexist-play-only: leverage existing footprint */}
      {report.relationship_leverage && (
        <Section icon={<Handshake size={14} />} label="Relationship leverage · existing footprint" emphasis>
          {report.relationship_leverage.existing_footprint && (
            <div className="drm-subblock">
              <div className="drm-subblock__label">Existing footprint</div>
              <Prose text={report.relationship_leverage.existing_footprint} />
            </div>
          )}
          {report.relationship_leverage.wedge_strategy && (
            <div className="drm-subblock">
              <div className="drm-subblock__label">Wedge strategy</div>
              <Prose text={report.relationship_leverage.wedge_strategy} />
            </div>
          )}
          {report.relationship_leverage.consolidation_dividend && (
            <div className="drm-subblock">
              <div className="drm-subblock__label">Consolidation dividend</div>
              <Prose text={report.relationship_leverage.consolidation_dividend} />
            </div>
          )}
        </Section>
      )}

      {/* Defend-play-only: retention risk + wallet-share expansion */}
      {report.retention_risk && (
        <Section icon={<AlertOctagon size={14} />} label="Retention risk · defend the renewal" tone="warn">
          {report.retention_risk.departure_signals && (
            <div className="drm-subblock">
              <div className="drm-subblock__label">Departure signals</div>
              {Array.isArray(report.retention_risk.departure_signals) ? (
                <ul className="drm-questions">
                  {report.retention_risk.departure_signals.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              ) : (
                <Prose text={report.retention_risk.departure_signals} />
              )}
            </div>
          )}
          {report.retention_risk.counter_moves && (
            <div className="drm-subblock">
              <div className="drm-subblock__label">Counter moves</div>
              {Array.isArray(report.retention_risk.counter_moves) ? (
                <ul className="drm-questions">
                  {report.retention_risk.counter_moves.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              ) : (
                <Prose text={report.retention_risk.counter_moves} />
              )}
            </div>
          )}
          {report.retention_risk.wallet_share_expansion && (
            <div className="drm-subblock">
              <div className="drm-subblock__label">Wallet-share expansion</div>
              <Prose text={report.retention_risk.wallet_share_expansion} />
            </div>
          )}
        </Section>
      )}

      {Array.isArray(report.recommended_next_steps) && report.recommended_next_steps.length > 0 && (
        <Section icon={<ListChecks size={14} />} label="Recommended next steps">
          <ol className="drm-steps">
            {report.recommended_next_steps.map((step, i) => (
              <li key={i}>
                <div className="drm-step__head">
                  <strong>{step.step || step.action || `Step ${i + 1}`}</strong>
                  {step.owner && <span className="drm-step__owner">{step.owner}</span>}
                </div>
                {step.outcome && <div className="drm-step__outcome">{step.outcome}</div>}
              </li>
            ))}
          </ol>
        </Section>
      )}

      {Array.isArray(report.open_questions) && report.open_questions.length > 0 && (
        <Section icon={<HelpCircle size={14} />} label="Open questions to validate">
          <ul className="drm-questions">
            {report.open_questions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </Section>
      )}

      {model && (
        <div className="drm-meta-row drm-meta-row--footer">
          <span>Model: {model}</span>
        </div>
      )}
    </div>
  );
}

function NotesEditor({
  value, onChange, onBlur, isPending, isError, errorMessage, saved, dirty, disabled, charLimit,
}) {
  const remaining = (charLimit || 2400) - (value?.length || 0);
  const overLimit = remaining < 0;
  return (
    <div className="drm-notes">
      <div className="drm-notes__head">
        <NotebookPen size={13} />
        <span className="drm-notes__label">Operator account intel</span>
        <span className="drm-notes__hint">
          Sponsor names, CIO background, recent org changes, what to avoid mentioning.
          Weighted heavily in the next briefing regeneration.
        </span>
        <span className="drm-notes__status">
          {isPending && <span className="drm-notes__saving"><Loader2 size={11} className="drm-spin" /> Saving…</span>}
          {!isPending && saved && <span className="drm-notes__saved"><Check size={11} /> Saved</span>}
          {!isPending && !saved && dirty && <span className="drm-notes__dirty">Unsaved</span>}
          {!isPending && isError && <span className="drm-notes__error">Save failed: {errorMessage}</span>}
        </span>
      </div>
      <textarea
        className={`drm-notes__textarea ${overLimit ? 'is-over-limit' : ''}`}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        placeholder="e.g. CIO Maria Chen joined from Anthem 6 months ago, prefers cloud-native architectures. Procurement officer is sponsoring through CDT. Avoid referencing the 2024 Splunk RFP — it failed for political reasons."
        rows={4}
      />
      <div className="drm-notes__foot">
        <span className={`drm-notes__count ${overLimit ? 'is-over' : ''}`}>
          {(value || '').length} / {charLimit || 2400} chars
          {overLimit && ' · text beyond the limit will be truncated in the prompt'}
        </span>
      </div>
    </div>
  );
}

function Section({ icon, label, tone, emphasis, children }) {
  const cls = [
    'drm-section',
    tone === 'warn' ? 'drm-section--warn' : '',
    emphasis ? 'drm-section--emphasis' : '',
  ].filter(Boolean).join(' ');
  return (
    <section className={cls}>
      <div className="drm-section-head">
        {icon}
        <span>{label}</span>
      </div>
      <div className="drm-section-body">{children}</div>
    </section>
  );
}

// Render multi-paragraph LLM prose preserving paragraph breaks. Opus
// occasionally returns a single string with \n\n separators; treat
// those as paragraphs and trim defensively.
function Prose({ text }) {
  if (!text) return null;
  const paragraphs = String(text)
    .split(/\n{2,}/)
    .map(p => p.trim())
    .filter(Boolean);
  if (paragraphs.length <= 1) return <p>{text}</p>;
  return (
    <>
      {paragraphs.map((p, i) => <p key={i}>{p}</p>)}
    </>
  );
}

// Render the structured Opus payload as portable Markdown for paste into
// email / Slack / Notion / Google Docs. Order matches what the modal
// displays so the printed and pasted versions stay in sync.
export function reportToMarkdown({ vendor, card, report, generatedAt, model }) {
  const lines = [];
  lines.push(`# CIO Executive Briefing — ${card?.title || vendor}`);
  const subtitleParts = [vendor];
  if (card?.seed_payload?.competitor) subtitleParts.push(`vs ${card.seed_payload.competitor}`);
  if (card?.seed_payload?.agency) subtitleParts.push(card.seed_payload.agency);
  lines.push(`_${subtitleParts.join(' · ')}_`);
  if (generatedAt) {
    const when = new Date(generatedAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
    lines.push(`_Generated ${when}${model ? ` by ${model}` : ''}_`);
  }
  lines.push('');

  const proseSection = (title, body) => {
    if (!body) return;
    lines.push(`## ${title}`);
    lines.push(body.trim());
    lines.push('');
  };

  proseSection('Executive summary', report.executive_summary);
  proseSection('Why do anything? — cost of the status quo', report.status_quo_risk);
  proseSection(`Why us? — distinctive value`, report.vendor_differentiation);
  proseSection('Install-base insights', report.install_base_insights);

  if (Array.isArray(report.business_line_capabilities) && report.business_line_capabilities.length) {
    lines.push('## Business-line capabilities');
    for (const cap of report.business_line_capabilities) {
      lines.push(`### ${cap.capability || cap.label || ''}`);
      if (cap.lob_value) lines.push(`- **LoB outcome:** ${cap.lob_value}`);
      if (cap.why_target_wins) lines.push(`- **Why we win:** ${cap.why_target_wins}`);
      lines.push('');
    }
  }

  proseSection('AI-era readiness · 3-5 year horizon', report.ai_era_readiness);
  proseSection('Migration risk · honest assessment', report.migration_risk_assessment);
  proseSection('Budget efficiency over 3-5 years', report.budget_efficiency);

  // Offensive-play entry motion
  if (report.entry_motion) {
    lines.push('## Entry motion · cold-start play');
    if (report.entry_motion.beachhead) {
      lines.push('**Beachhead deployment**');
      lines.push(report.entry_motion.beachhead.trim());
      lines.push('');
    }
    if (report.entry_motion.procurement_vehicle) {
      lines.push('**Procurement vehicle**');
      lines.push(report.entry_motion.procurement_vehicle.trim());
      lines.push('');
    }
    if (report.entry_motion.peer_references) {
      lines.push('**Peer references**');
      if (Array.isArray(report.entry_motion.peer_references)) {
        for (const p of report.entry_motion.peer_references) lines.push(`- ${p}`);
      } else {
        lines.push(report.entry_motion.peer_references.trim());
      }
      lines.push('');
    }
    if (report.entry_motion.time_to_first_value) {
      lines.push('**Time to first value**');
      lines.push(report.entry_motion.time_to_first_value.trim());
      lines.push('');
    }
  }

  // Coexist-play relationship leverage
  if (report.relationship_leverage) {
    lines.push('## Relationship leverage · existing footprint');
    for (const [key, label] of [
      ['existing_footprint', 'Existing footprint'],
      ['wedge_strategy', 'Wedge strategy'],
      ['consolidation_dividend', 'Consolidation dividend'],
    ]) {
      const v = report.relationship_leverage[key];
      if (v) {
        lines.push(`**${label}**`);
        lines.push(String(v).trim());
        lines.push('');
      }
    }
  }

  // Defend-play retention risk
  if (report.retention_risk) {
    lines.push('## Retention risk · defend the renewal');
    if (report.retention_risk.departure_signals) {
      lines.push('**Departure signals**');
      if (Array.isArray(report.retention_risk.departure_signals)) {
        for (const s of report.retention_risk.departure_signals) lines.push(`- ${s}`);
      } else {
        lines.push(String(report.retention_risk.departure_signals).trim());
      }
      lines.push('');
    }
    if (report.retention_risk.counter_moves) {
      lines.push('**Counter moves**');
      if (Array.isArray(report.retention_risk.counter_moves)) {
        for (const s of report.retention_risk.counter_moves) lines.push(`- ${s}`);
      } else {
        lines.push(String(report.retention_risk.counter_moves).trim());
      }
      lines.push('');
    }
    if (report.retention_risk.wallet_share_expansion) {
      lines.push('**Wallet-share expansion**');
      lines.push(String(report.retention_risk.wallet_share_expansion).trim());
      lines.push('');
    }
  }

  if (Array.isArray(report.recommended_next_steps) && report.recommended_next_steps.length) {
    lines.push('## Recommended next steps');
    report.recommended_next_steps.forEach((step, i) => {
      const head = step.step || step.action || `Step ${i + 1}`;
      const owner = step.owner ? ` _(${step.owner})_` : '';
      lines.push(`${i + 1}. **${head}**${owner}`);
      if (step.outcome) lines.push(`   ${step.outcome}`);
    });
    lines.push('');
  }

  if (Array.isArray(report.open_questions) && report.open_questions.length) {
    lines.push('## Open questions to validate');
    for (const q of report.open_questions) lines.push(`- ${q}`);
    lines.push('');
  }

  lines.push('---');
  lines.push('_Verify all claims with the customer before committing in writing — LLMs can confidently misstate facts._');
  return lines.join('\n');
}

export default DisplacementReportModal;
